'use client';

import { useState } from 'react';
import { X, GitCompare, Plus, Check, Trash2, ShoppingBag } from 'lucide-react';
import { PRODUCTS, Product } from '@/lib/products';
import { getAverageRating, getReviewCount } from '@/lib/reviews';
import { Star } from 'lucide-react';
import { useCart } from '@/lib/cart-store';
import { useCompareUI } from '@/lib/compare-ui-store';

const COMPARISON_CRITERIA = [
  { key: 'price', label: 'Prix', format: (p: Product) => `${p.price.toLocaleString('fr-BE')} €` },
  { key: 'category', label: 'Catégorie', format: (p: Product) => p.category },
  { key: 'rating', label: 'Note moyenne', format: (p: Product) => {
    const avg = getAverageRating(p.id);
    const cnt = getReviewCount(p.id);
    return cnt > 0 ? `${avg.toFixed(1)} ★ (${cnt})` : 'Nouveau';
  }},
  { key: 'tag', label: 'Statut', format: (p: Product) => p.tag || 'Standard' },
  { key: 'stock', label: 'Disponibilité', format: (p: Product) => p.inStock ? 'En stock' : 'Sur commande' },
  { key: 'warranty', label: 'Garantie', format: () => 'À vie' },
  { key: 'numbering', label: 'Numérotée', format: () => 'Oui · Certificat' },
  { key: 'origin', label: 'Origine', format: () => 'Anvers · Belgique' },
];

export default function CompareModal() {
  const { isOpen, close } = useCompareUI();
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [showSelector, setShowSelector] = useState(false);
  const [search, setSearch] = useState('');
  const { addItem, openCart } = useCart();

  const compareProducts = compareIds
    .map((id) => PRODUCTS.find((p) => p.id === id))
    .filter(Boolean) as Product[];

  const filteredProducts = PRODUCTS.filter(
    (p) =>
      !compareIds.includes(p.id) &&
      (search.trim().length < 2 ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.category.toLowerCase().includes(search.toLowerCase()))
  ).slice(0, 8);

  const addToCompare = (id: string) => {
    if (compareIds.length >= 4) return;
    if (compareIds.includes(id)) return;
    setCompareIds([...compareIds, id]);
    setShowSelector(false);
    setSearch('');
  };

  const removeFromCompare = (id: string) => {
    setCompareIds(compareIds.filter((cid) => cid !== id));
  };

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      category: product.category,
    });
    close();
    openCart();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={() => close()}
    >
      <div
        className="relative bg-[#0a0a0f] border border-[#D4AF37]/30 max-w-6xl w-full my-auto max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0a0a0f] border-b border-[#D4AF37]/20 p-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <GitCompare size={20} className="text-[#D4AF37]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">
                Comparateur
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-[#F4E8C1]">
                Comparer les pièces
              </h2>
            </div>
          </div>
          <button
            onClick={() => close()}
            className="w-9 h-9 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 sm:p-7">
          {compareProducts.length === 0 ? (
            <div className="text-center py-12">
              <GitCompare size={48} className="text-[#D4AF37]/30 mx-auto mb-5" />
              <p className="font-serif italic text-lg text-[#F4E8C1]/60 mb-2">
                Aucune pièce à comparer
              </p>
              <p className="text-xs text-[#F4E8C1]/45 max-w-md mx-auto mb-7 leading-relaxed">
                Sélectionnez jusqu'à 4 pièces pour les comparer côte à côte :
                prix, catégorie, note, garantie, origine.
              </p>
            </div>
          ) : (
            <>
              {/* Grid: criteria × products */}
              <div className="overflow-x-auto">
                <div
                  className="grid gap-3 min-w-full"
                  style={{
                    gridTemplateColumns: `140px repeat(${compareProducts.length}, minmax(180px, 1fr))`,
                  }}
                >
                  {/* Header row */}
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/70 flex items-end pb-2 border-b border-[#D4AF37]/15">
                    Critères
                  </div>
                  {compareProducts.map((p) => (
                    <div
                      key={p.id}
                      className="relative pb-2 border-b border-[#D4AF37]/15"
                    >
                      <button
                        onClick={() => removeFromCompare(p.id)}
                        className="absolute top-0 right-0 w-6 h-6 flex items-center justify-center text-[#F4E8C1]/45 hover:text-[#CE1126] transition-colors"
                        aria-label="Retirer"
                      >
                        <X size={12} />
                      </button>
                      <div className="aspect-square mb-2 overflow-hidden border border-[#D4AF37]/20">
                        <img
                          src={p.image}
                          alt={p.name}
                          className="w-full h-full object-cover"
                          style={{ filter: 'brightness(0.85) saturate(1.05)' }}
                        />
                      </div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/85 mb-1">
                        {p.category}
                      </div>
                      <h4 className="font-display text-sm font-semibold text-[#F4E8C1] leading-tight pr-5">
                        {p.name}
                      </h4>
                    </div>
                  ))}

                  {/* Criteria rows */}
                  {COMPARISON_CRITERIA.map((crit) => (
                    <div key={crit.key} className="contents">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/65 py-3 border-b border-[#D4AF37]/10 font-medium flex items-center">
                        {crit.label}
                      </div>
                      {compareProducts.map((p) => (
                        <div
                          key={`${crit.key}-${p.id}`}
                          className="py-3 border-b border-[#D4AF37]/10 flex items-center"
                        >
                          {crit.key === 'rating' ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-0.5">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <Star
                                    key={s}
                                    size={10}
                                    className={s <= Math.round(getAverageRating(p.id)) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#D4AF37]/25'}
                                  />
                                ))}
                              </div>
                              <span className="text-xs text-[#F4E8C1] font-medium">
                                {getReviewCount(p.id) > 0 ? getAverageRating(p.id).toFixed(1) : '—'}
                              </span>
                            </div>
                          ) : crit.key === 'price' ? (
                            <span className="font-display text-sm font-bold text-gold-gradient">
                              {crit.format(p)}
                            </span>
                          ) : (
                            <span className="text-xs text-[#F4E8C1]/80 font-light">
                              {crit.format(p)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Add to cart row */}
                  <div className="py-3 flex items-center">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/65 font-medium">
                      Commander
                    </span>
                  </div>
                  {compareProducts.map((p) => (
                    <div key={`cart-${p.id}`} className="py-3">
                      <button
                        onClick={() => handleAddToCart(p)}
                        className="w-full inline-flex items-center justify-center gap-2 py-2.5 border border-[#D4AF37]/30 text-[#F4E8C1] hover:bg-gold-gradient hover:text-[#0a0a0f] hover:border-[#D4AF37] transition-all text-[10px] uppercase tracking-[0.2em] font-semibold"
                      >
                        <ShoppingBag size={11} />
                        Ajouter
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add more */}
              {compareProducts.length < 4 && (
                <button
                  onClick={() => setShowSelector(true)}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 border border-dashed border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all text-xs uppercase tracking-[0.2em] font-semibold"
                >
                  <Plus size={14} />
                  Ajouter une pièce à comparer ({compareProducts.length}/4)
                </button>
              )}
            </>
          )}

          {/* Product selector */}
          {showSelector && (
            <div className="mt-6 bg-[#12121a] border border-[#D4AF37]/25 p-5">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-display text-sm font-semibold text-[#F4E8C1] tracking-wide uppercase">
                  Choisir une pièce
                </h4>
                <button
                  onClick={() => setShowSelector(false)}
                  className="text-[#F4E8C1]/45 hover:text-[#F4E8C1] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                autoFocus
                className="w-full bg-[#0a0a0f] border border-[#D4AF37]/25 px-4 py-2.5 text-sm text-[#F4E8C1] placeholder:text-[#F4E8C1]/30 focus:border-[#D4AF37] focus:outline-none mb-4"
              />

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-h-72 overflow-y-auto">
                {filteredProducts.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addToCompare(p.id)}
                    className="flex items-center gap-3 p-3 border border-[#D4AF37]/20 hover:border-[#D4AF37]/55 hover:bg-[#0a0a0f]/60 transition-all text-left"
                  >
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-12 h-12 object-cover border border-[#D4AF37]/20 flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#D4AF37]/85">
                        {p.category}
                      </div>
                      <div className="text-xs font-medium text-[#F4E8C1] truncate">
                        {p.name}
                      </div>
                      <div className="font-display text-xs text-gold-gradient font-bold mt-0.5">
                        {p.price.toLocaleString('fr-BE')} €
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
